import { useMemo } from "react";
import { Tenant } from "@/hooks/useAuth";

export interface ProfileField {
  key: string;
  label: string;
  weight: number;
  category: "basic" | "address" | "media" | "location" | "verification";
  isComplete: boolean;
}

export function useProfileCompletion(tenant: Tenant | null) {
  // Cast to access new verification fields
  const tenantWithVerification = tenant as (Tenant & {
    phone_verified?: boolean;
    email_verified?: boolean;
  }) | null;

  const fields = useMemo<ProfileField[]>(() => {
    if (!tenantWithVerification) return [];
    
    return [
      // Basic Info (30 points)
      { key: "name", label: "Business Name", weight: 6, category: "basic", isComplete: !!tenantWithVerification.name },
      { key: "vle_name", label: "VLE Name", weight: 6, category: "basic", isComplete: !!tenantWithVerification.vle_name },
      { key: "phone", label: "Mobile Number", weight: 6, category: "basic", isComplete: !!tenantWithVerification.phone },
      { key: "email", label: "Email", weight: 6, category: "basic", isComplete: !!tenantWithVerification.email },
      { key: "business_type", label: "Business Category", weight: 6, category: "basic", isComplete: !!tenantWithVerification.business_type },
      
      // Verification (20 points) - MANDATORY for publishing
      { key: "phone_verified", label: "Mobile Verified", weight: 10, category: "verification", isComplete: !!tenantWithVerification.phone_verified },
      { key: "email_verified", label: "Email Verified", weight: 10, category: "verification", isComplete: !!tenantWithVerification.email_verified },
      
      // Address (25 points)
      { key: "state", label: "State", weight: 5, category: "address", isComplete: !!tenantWithVerification.state },
      { key: "district", label: "District", weight: 5, category: "address", isComplete: !!tenantWithVerification.district },
      { key: "taluka", label: "Taluka/Block", weight: 5, category: "address", isComplete: !!tenantWithVerification.taluka },
      { key: "village", label: "Village", weight: 5, category: "address", isComplete: !!tenantWithVerification.village },
      { key: "pincode", label: "Pincode", weight: 5, category: "address", isComplete: !!tenantWithVerification.pincode },
      
      // Media (15 points)
      { key: "vle_photo_url", label: "VLE Photo", weight: 8, category: "media", isComplete: !!tenantWithVerification.vle_photo_url },
      { key: "shop_photos", label: "Shop Photos", weight: 7, category: "media", isComplete: (tenantWithVerification.shop_photos?.length ?? 0) > 0 },
      
      // Location (10 points)
      { key: "location", label: "Map Location", weight: 10, category: "location", isComplete: !!tenantWithVerification.latitude && !!tenantWithVerification.longitude },
    ];
  }, [tenantWithVerification]);

  const completionPercentage = useMemo(() => {
    if (!tenantWithVerification) return 0;
    const totalWeight = fields.reduce((sum, f) => sum + f.weight, 0);
    const completedWeight = fields.filter(f => f.isComplete).reduce((sum, f) => sum + f.weight, 0);
    return Math.round((completedWeight / totalWeight) * 100);
  }, [fields, tenantWithVerification]);

  const incompleteFields = useMemo(() => {
    return fields.filter(f => !f.isComplete);
  }, [fields]);

  // For publishing: need 100% completion which includes verification
  const isPublishable = completionPercentage >= 100;

  // Additional checks for verification status
  const isPhoneVerified = !!tenantWithVerification?.phone_verified;
  const isEmailVerified = !!tenantWithVerification?.email_verified;
  const isVerified = isPhoneVerified && isEmailVerified;

  return {
    fields,
    completionPercentage,
    incompleteFields,
    isPublishable,
    isPhoneVerified,
    isEmailVerified,
    isVerified,
  };
}